import { useState, useMemo, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { useToast } from '@/hooks/use-toast'
import { explainError } from '@/lib/explain-error'
import { Download, Upload, Loader2, AlertCircle, Search } from 'lucide-react'
import type { PriceMatrix, PriceMatrixImportPreview } from '@/types'

/**
 * Products down, governed currencies across.
 *
 * The per-currency editor could never show that a product's Australian price is
 * the same number as its US price — which is the state 54 products are in,
 * because someone copied rather than converted. Seeing the currencies side by
 * side is the only way that is obvious rather than invisible.
 *
 * Editing goes through the existing bulk endpoint, so every change here lands
 * in the price-change history and can be reverted like any other.
 */
interface PriceMatrixPanelProps {
  pricebookId: string
}

/** Staged edits, keyed `productId__ISO`, held until Save. */
type Draft = Record<string, string>

export function PriceMatrixPanel({ pricebookId }: PriceMatrixPanelProps) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const fileInput = useRef<HTMLInputElement>(null)

  const [search, setSearch] = useState('')
  const [onlyGaps, setOnlyGaps] = useState(false)
  const [draft, setDraft] = useState<Draft>({})
  const [isSaving, setIsSaving] = useState(false)
  const [importPreview, setImportPreview] = useState<PriceMatrixImportPreview | null>(null)

  const { data, isLoading, error } = useQuery<PriceMatrix>({
    queryKey: ['price-matrix', pricebookId, search, onlyGaps],
    queryFn: () =>
      apiClient.getPriceMatrix(pricebookId, {
        search: search || undefined,
        only_gaps: onlyGaps || undefined,
      }),
  })

  const currencies = data?.currencies ?? []
  const rows = data?.rows ?? []
  const dirtyCount = Object.keys(draft).length

  const cellKey = (productId: string, iso: string) => `${productId}__${iso}`

  /** The value to show: a staged edit if there is one, else the stored price. */
  const displayValue = (productId: string, iso: string) => {
    const key = cellKey(productId, iso)
    if (key in draft) return draft[key]
    const cell = rows.find((r) => r.id === productId)?.prices[iso]
    if (!cell || cell.status !== 'set' || cell.unit_price === null) return ''
    return String(cell.unit_price)
  }

  const setCell = (productId: string, iso: string, value: string) => {
    setDraft((prev) => ({ ...prev, [cellKey(productId, iso)]: value }))
  }

  /** Turn the staged edits into the bulk-editor payload. */
  const buildPayload = () => {
    const creates: Array<{
      product_id: string
      currency_iso_code: string
      unit_price: number
    }> = []
    const updates: Array<{ id: string; unit_price: number }> = []
    const invalid: string[] = []

    for (const [key, raw] of Object.entries(draft)) {
      // split() is typed string | undefined per element; both are required.
      const [productId, iso] = key.split('__')
      if (!productId || !iso) continue
      const row = rows.find((r) => r.id === productId)
      if (!row) continue
      const cell = row.prices[iso]
      const trimmed = raw.trim()

      // Clearing a box is not a delete — deleting a price because a field was
      // blanked is the kind of silent destruction this work removed elsewhere.
      // Removing a price is a deliberate action in the entries editor.
      if (trimmed === '') continue

      const value = Number(trimmed)
      if (!Number.isFinite(value) || value < 0) {
        invalid.push(`${row.name} (${iso})`)
        continue
      }

      if (cell?.status === 'set' && cell.pricebook_entry_id) {
        if (Number(cell.unit_price) !== value) {
          updates.push({ id: cell.pricebook_entry_id, unit_price: value })
        }
      } else {
        creates.push({ product_id: productId, currency_iso_code: iso, unit_price: value })
      }
    }
    return { creates, updates, invalid }
  }

  const handleSave = async () => {
    const { creates, updates, invalid } = buildPayload()

    if (invalid.length > 0) {
      toast({
        title: 'Some prices are not valid numbers',
        description: `Fix these before saving: ${invalid.slice(0, 5).join(', ')}${
          invalid.length > 5 ? ` and ${invalid.length - 5} more` : ''
        }`,
        variant: 'destructive',
      })
      return
    }
    if (creates.length === 0 && updates.length === 0) {
      toast({ title: 'Nothing to save', description: 'No prices were changed.' })
      setDraft({})
      return
    }

    setIsSaving(true)
    try {
      const result = await apiClient.bulkPatchPricebookEntries(pricebookId, {
        creates,
        updates,
      })
      const applied = (result.created ?? 0) + (result.updated ?? 0)
      const rejected = result.rejected ?? []

      if (rejected.length > 0) {
        toast({
          title: `${applied} price${applied === 1 ? '' : 's'} saved, ${rejected.length} rejected`,
          description: rejected[0]?.reason ?? 'Some rows could not be applied.',
          variant: 'destructive',
        })
      } else {
        toast({
          title: 'Prices saved',
          description: `${applied} price${applied === 1 ? '' : 's'} updated. You can undo this from the change history.`,
        })
      }
      setDraft({})
      queryClient.invalidateQueries({ queryKey: ['price-matrix', pricebookId] })
      queryClient.invalidateQueries({ queryKey: ['pricebook-entries', pricebookId] })
      queryClient.invalidateQueries({ queryKey: ['products'] })
    } catch (err) {
      toast({
        title: 'Could not save prices',
        description: explainError(err, 'The price list is unchanged.'),
        variant: 'destructive',
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleExport = async () => {
    try {
      const csv = await apiClient.exportPriceMatrix(pricebookId, {
        search: search || undefined,
      })
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'price-list.csv'
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      toast({
        title: 'Could not export the price list',
        description: explainError(err, 'Please try again.'),
        variant: 'destructive',
      })
    }
  }

  const handleFileChosen = async (file: File) => {
    try {
      const csv = await file.text()
      const preview = await apiClient.previewPriceMatrixImport(pricebookId, csv)
      setImportPreview(preview)
    } catch (err) {
      toast({
        title: 'Could not read that file',
        description: explainError(err, 'Check it is a CSV exported from this page.'),
        variant: 'destructive',
      })
    }
  }

  const applyImport = async () => {
    if (!importPreview) return
    setIsSaving(true)
    try {
      const result = await apiClient.bulkPatchPricebookEntries(pricebookId, {
        creates: importPreview.creates,
        updates: importPreview.updates,
      })
      toast({
        title: 'Import applied',
        description: `${(result.created ?? 0) + (result.updated ?? 0)} prices updated. You can undo this from the change history.`,
      })
      setImportPreview(null)
      queryClient.invalidateQueries({ queryKey: ['price-matrix', pricebookId] })
      queryClient.invalidateQueries({ queryKey: ['products'] })
    } catch (err) {
      toast({
        title: 'Could not apply the import',
        description: explainError(err, 'The price list is unchanged.'),
        variant: 'destructive',
      })
    } finally {
      setIsSaving(false)
    }
  }

  const coverage = data?.summary.coverage ?? {}
  const totalGaps = useMemo(
    () => Object.values(coverage).reduce((n, c) => n + c.missing, 0),
    [coverage]
  )

  return (
    <div className="space-y-4">
      {/* Coverage — the size of the problem, on the page rather than in a report */}
      <div className="flex flex-wrap gap-3">
        {currencies.map((c) => {
          const cov = coverage[c.iso_code]
          if (!cov) return null
          const total = cov.set + cov.missing
          return (
            <Card key={c.iso_code} className="flex-1 min-w-[180px]">
              <CardContent className="p-3">
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{c.iso_code}</span>
                  {c.is_pricing_base && (
                    <Badge variant="outline" className="text-xs">
                      pricing base
                    </Badge>
                  )}
                </div>
                <p className="text-2xl font-bold mt-1">
                  {cov.set}
                  <span className="text-sm font-normal text-muted-foreground"> / {total}</span>
                </p>
                <p className="text-xs text-muted-foreground">
                  prices set
                  {cov.missing > 0 && ` · ${cov.missing} missing`}
                </p>
              </CardContent>
            </Card>
          )
        })}
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search
            className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            className="pl-8"
            placeholder="Search products…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search products in the price list"
          />
        </div>
        <Button
          variant={onlyGaps ? 'default' : 'outline'}
          onClick={() => setOnlyGaps((v) => !v)}
          aria-pressed={onlyGaps}
        >
          Needs a price{totalGaps > 0 ? ` (${totalGaps})` : ''}
        </Button>
        <Button variant="outline" onClick={handleExport}>
          <Download className="mr-2 h-4 w-4" aria-hidden="true" />
          Export CSV
        </Button>
        <Button variant="outline" onClick={() => fileInput.current?.click()}>
          <Upload className="mr-2 h-4 w-4" aria-hidden="true" />
          Import CSV
        </Button>
        <input
          ref={fileInput}
          type="file"
          accept=".csv,text/csv"
          className="sr-only"
          aria-label="Choose a CSV file to import"
          onChange={(e) => {
            const f = e.target.files?.[0]
            if (f) handleFileChosen(f)
            e.target.value = ''
          }}
        />
        {dirtyCount > 0 && (
          <>
            <Button variant="ghost" onClick={() => setDraft({})} disabled={isSaving}>
              Discard
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
              Save {dirtyCount} change{dirtyCount === 1 ? '' : 's'}
            </Button>
          </>
        )}
      </div>

      {/* Import preview — nothing has been written at this point */}
      {importPreview && (
        <Card role="region" aria-label="Import preview">
          <CardContent className="p-4 space-y-3">
            <h3 className="font-semibold">Import preview — nothing has been changed yet</h3>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>{importPreview.creates.length} new price(s)</li>
              <li>{importPreview.updates.length} price(s) changed</li>
              <li>{importPreview.unchanged} unchanged (no action)</li>
              {importPreview.errors.length > 0 && (
                <li className="text-destructive">
                  {importPreview.errors.length} row(s) could not be read:
                  <ul className="list-disc list-inside ml-2">
                    {importPreview.errors.slice(0, 5).map((e, i) => (
                      <li key={i}>
                        {e.row > 0 ? `Row ${e.row}: ` : ''}
                        {e.reason}
                      </li>
                    ))}
                  </ul>
                </li>
              )}
            </ul>
            <p className="text-xs text-muted-foreground">
              Blank cells are left alone — importing never deletes a price.
            </p>
            <div className="flex gap-2">
              <Button
                onClick={applyImport}
                disabled={isSaving || importPreview.total_changes === 0}
              >
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
                Apply {importPreview.total_changes} change
                {importPreview.total_changes === 1 ? '' : 's'}
              </Button>
              <Button variant="ghost" onClick={() => setImportPreview(null)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* The grid */}
      {isLoading ? (
        <div className="py-12 text-center text-muted-foreground" role="status" aria-live="polite">
          <Loader2 className="h-4 w-4 animate-spin inline mr-2" aria-hidden="true" />
          Loading prices…
        </div>
      ) : error ? (
        <div className="py-12 text-center text-destructive" role="alert">
          {explainError(error, 'Could not load the price list.')}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <caption className="sr-only">
                  Product prices by currency. Empty boxes mean no price is set; converted values
                  shown underneath are estimates, not managed prices.
                </caption>
                <thead className="bg-muted/50">
                  <tr className="border-b">
                    <th scope="col" className="text-left p-3 font-medium">
                      Product
                    </th>
                    {currencies.map((c) => (
                      <th key={c.iso_code} scope="col" className="text-left p-3 font-medium w-40">
                        {c.iso_code}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr>
                      <td
                        colSpan={currencies.length + 1}
                        className="p-6 text-center text-muted-foreground"
                      >
                        No products match.
                      </td>
                    </tr>
                  )}
                  {rows.map((row) => (
                    <tr key={row.id} className="border-b last:border-b-0">
                      <th scope="row" className="text-left p-3 font-normal">
                        {row.name}
                        {row.product_code && (
                          <span className="block text-xs text-muted-foreground">
                            {row.product_code}
                          </span>
                        )}
                      </th>
                      {currencies.map((c) => {
                        const cell = row.prices[c.iso_code]
                        const key = cellKey(row.id, c.iso_code)
                        const isDirty = key in draft
                        return (
                          <td key={c.iso_code} className="p-2 align-top">
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              inputMode="decimal"
                              className={`h-8 ${isDirty ? 'border-primary' : ''}`}
                              value={displayValue(row.id, c.iso_code)}
                              placeholder={cell?.status === 'missing' ? 'No price' : ''}
                              onChange={(e) => setCell(row.id, c.iso_code, e.target.value)}
                              aria-label={`${c.iso_code} price for ${row.name}`}
                              aria-describedby={`${key}-status`}
                            />
                            <p
                              id={`${key}-status`}
                              className={`text-xs mt-1 ${
                                cell?.status === 'missing'
                                  ? 'text-destructive'
                                  : 'text-muted-foreground'
                              }`}
                            >
                              {cell?.status === 'set' && 'Set'}
                              {cell?.status === 'missing' && (
                                <>
                                  <AlertCircle className="h-3 w-3 inline mr-1" aria-hidden="true" />
                                  No {c.iso_code} price
                                </>
                              )}
                            </p>
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
